/**
 * Turning a click into a point on the model's surface.
 *
 * The whole value of measurement rests on this file. Measuring the distance
 * between two *screen* positions would be measuring the picture, not the
 * object: it changes when the camera orbits, changes when the user zooms, and
 * is a different number at every viewing angle. A length has to come from
 * where the surface actually is in three dimensions, which means a ray from
 * the eye through the pixel, intersected with the triangles.
 *
 * ## The ray
 *
 * Built from the camera directly rather than by inverting the MVP matrix. The
 * basis here is copied from `mvpMatrix` deliberately - same forward, same
 * right, same up, same 50-degree field of view - because a picker that
 * disagrees with the renderer about where the camera is points at a different
 * pixel than the one the user clicked, and the error is small enough to look
 * like imprecision rather than a bug.
 *
 * ## The intersection
 *
 * Möller-Trumbore, front and back faces alike. A reconstruction is an open
 * surface and the inside of a bowl is a real place to measure; culling back
 * faces would make half of many models unpickable.
 *
 * The nearest hit wins. Not the first found - triangles arrive in index
 * order, which has nothing to do with depth, and taking the first would let a
 * far wall win over the near one it is behind.
 */

import { cross, dot, eyeOf, normalise, type Camera, type Vec3 } from "./viewerCamera";
import { FOV_DEGREES } from "./viewerFraming";

export interface Ray {
  origin: Vec3;
  /** Unit length. */
  direction: Vec3;
}

export interface SurfaceHit {
  /** Where the surface is, in model space. */
  point: Vec3;
  /** Distance along the ray, for choosing the nearest of several hits. */
  distance: number;
  /** Index into `indices` of the triangle's first corner. */
  triangle: number;
}

/**
 * The ray through one pixel of the canvas.
 *
 * `x` and `y` are CSS pixels relative to the canvas's top-left corner, which
 * is what a pointer event gives after subtracting the bounding rect. Device
 * pixel ratio deliberately does not appear: the ray depends on where in the
 * *frame* the click was, not on how many physical pixels the frame is drawn
 * with, and folding the ratio in here is a bug that only shows on a retina
 * screen.
 */
export function rayThroughPixel(
  camera: Camera,
  x: number,
  y: number,
  width: number,
  height: number,
): Ray {
  const eye = eyeOf(camera);
  const forward = normalise([
    camera.target[0] - eye[0],
    camera.target[1] - eye[1],
    camera.target[2] - eye[2],
  ]);
  const right = normalise(cross(forward, [0, 1, 0]));
  const up = cross(right, forward);

  // Normalised device coordinates. Y is flipped because a canvas counts
  // downwards from the top and the projection counts upwards from the centre.
  const ndcX = (2 * x) / Math.max(width, 1) - 1;
  const ndcY = 1 - (2 * y) / Math.max(height, 1);

  const aspect = Math.max(width, 1) / Math.max(height, 1);
  const tangent = Math.tan(((FOV_DEGREES * Math.PI) / 180) / 2);

  const direction = normalise([
    forward[0] + right[0] * ndcX * tangent * aspect + up[0] * ndcY * tangent,
    forward[1] + right[1] * ndcX * tangent * aspect + up[1] * ndcY * tangent,
    forward[2] + right[2] * ndcX * tangent * aspect + up[2] * ndcY * tangent,
  ]);
  return { origin: eye, direction };
}

const EPSILON = 1e-12;

/**
 * Möller-Trumbore, without back-face culling.
 *
 * Returns the distance along the ray, or `null` when the ray misses. A hit
 * behind the eye is a miss: the triangle is real but it is not what was
 * clicked.
 */
export function intersectTriangle(
  ray: Ray,
  a: Vec3,
  b: Vec3,
  c: Vec3,
): number | null {
  const edge1: Vec3 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const edge2: Vec3 = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
  const pvec = cross(ray.direction, edge2);
  const determinant = dot(edge1, pvec);
  // Parallel to the triangle's plane. Not culled by sign, which is what keeps
  // the inside of a surface pickable.
  if (Math.abs(determinant) < EPSILON) return null;

  const inverse = 1 / determinant;
  const tvec: Vec3 = [
    ray.origin[0] - a[0],
    ray.origin[1] - a[1],
    ray.origin[2] - a[2],
  ];
  const u = dot(tvec, pvec) * inverse;
  if (u < 0 || u > 1) return null;

  const qvec = cross(tvec, edge1);
  const v = dot(ray.direction, qvec) * inverse;
  if (v < 0 || u + v > 1) return null;

  const distance = dot(edge2, qvec) * inverse;
  return distance > EPSILON ? distance : null;
}

/**
 * The nearest surface point along a ray, or `null` for a click on empty space.
 *
 * Linear over the triangles. A spatial index would be faster and is not worth
 * it yet: this runs once per click, not per frame, and a 200k-triangle model
 * takes a few milliseconds - well inside the time between a mouse-down and
 * anybody noticing.
 *
 * `clip` lets a section view hide geometry from the picker as well as from
 * the renderer. Being able to measure a triangle that is not on screen is the
 * exact defect it prevents.
 */
export function pickSurface(
  ray: Ray,
  positions: Float32Array,
  indices: Uint32Array,
  clip?: (point: Vec3) => boolean,
): SurfaceHit | null {
  let best: SurfaceHit | null = null;
  for (let i = 0; i + 2 < indices.length; i += 3) {
    const ia = indices[i]! * 3;
    const ib = indices[i + 1]! * 3;
    const ic = indices[i + 2]! * 3;
    const a: Vec3 = [positions[ia]!, positions[ia + 1]!, positions[ia + 2]!];
    const b: Vec3 = [positions[ib]!, positions[ib + 1]!, positions[ib + 2]!];
    const c: Vec3 = [positions[ic]!, positions[ic + 1]!, positions[ic + 2]!];

    const distance = intersectTriangle(ray, a, b, c);
    if (distance === null || (best !== null && distance >= best.distance)) continue;

    const point: Vec3 = [
      ray.origin[0] + ray.direction[0] * distance,
      ray.origin[1] + ray.direction[1] * distance,
      ray.origin[2] + ray.direction[2] * distance,
    ];
    if (clip && !clip(point)) continue;
    best = { point, distance, triangle: i };
  }
  return best;
}

/**
 * Move a picked point onto the nearest vertex of the triangle it landed on.
 *
 * Offered because somebody measuring an edge for a print wants the corner,
 * not a point four pixels inside the face next to it. Only ever within the
 * hit triangle: searching the whole mesh would snap across a gap to a vertex
 * the user could not see, which is worse than not snapping at all.
 */
export function snapToVertex(
  hit: SurfaceHit,
  positions: Float32Array,
  indices: Uint32Array,
): Vec3 {
  let best: Vec3 = hit.point;
  let bestDistance = Infinity;
  for (let corner = 0; corner < 3; corner += 1) {
    const base = indices[hit.triangle + corner]! * 3;
    const candidate: Vec3 = [
      positions[base]!,
      positions[base + 1]!,
      positions[base + 2]!,
    ];
    const d = Math.hypot(
      candidate[0] - hit.point[0],
      candidate[1] - hit.point[1],
      candidate[2] - hit.point[2],
    );
    if (d < bestDistance) {
      bestDistance = d;
      best = candidate;
    }
  }
  return best;
}

/** Straight-line distance between two model-space points. */
export function modelDistance(a: Vec3, b: Vec3): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}
